import React from "react";
import { useEffect, useRef, useState, Fragment, ChangeEvent } from "react";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { FaCog, FaEllipsisH } from "react-icons/fa";
import styles from "./styles.module.css";
import { useIsFetching } from "@tanstack/react-query";
import { Rings } from "react-loader-spinner";

interface Column {
  field: string;
  title: string;
  render?: any;
}

interface CustomTableProps {
  data: Record<string, any>[];
  columns: Column[];
  showCheckbox?: boolean;
  showFilter?: boolean;
  selectID?: string;
  onSelectedRowsChange?: (selectedRows: string[]) => void;
  clearSelections?: boolean;
  actions?: any;
  paperOrientation?: "portrait" | "landscape";
  paperSize?: "a4" | "letter";
  filename?: string;
  reverseAction?: boolean;

  exportBtns?: boolean;
}

const CustomTable: React.FC<CustomTableProps> = ({
  data = [],
  columns = [],
  showCheckbox = false,
  showFilter = false,
  selectID = "_id",
  onSelectedRowsChange = () => {},
  clearSelections = false,
  actions = () => [],
  paperOrientation = "landscape",
  paperSize = "a4",
  filename = "download",
  reverseAction = false,

  exportBtns = true,
}) => {
  const isFetching = useIsFetching();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (clearSelections) setSelectedRows([]);
  }, [clearSelections]);

  const getColumnValue = (item: Record<string, any>, field: string): any =>
    field.split(".").reduce((obj, key) => obj?.[key], item);

  const filteredData = data.filter((item) => {
    const matchesSearch = Object.values(item).some(
      (val) =>
        typeof val === "string" &&
        val.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const matchesFilters = Object.entries(filterValues).every(
      ([field, filterValue]) =>
        getColumnValue(item, field)
          ?.toLowerCase()
          .includes(filterValue.toLowerCase())
    );
    return matchesSearch && matchesFilters;
  });

  const handleRowSelect = (rowId: string, isSelected: boolean) => {
    const updatedRows = isSelected
      ? [...selectedRows, rowId]
      : selectedRows.filter((id) => id !== rowId);
    setSelectedRows(updatedRows);
    onSelectedRowsChange(updatedRows);
  };

  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: paperOrientation, format: paperSize });
    //@ts-ignore
    doc.autoTable({
      head: [columns.map((col) => col.title)],
      body: filteredData.map((item) =>
        columns.map((col) => getColumnValue(item, col.field))
      ),
    });
    doc.save(`${filename}.pdf`);
  };

  const downloadCSV = () => {
    const csvContent = `${columns
      .map((col) => col.title)
      .join(",")}\n${filteredData
      .map((item) =>
        columns.map((col) => getColumnValue(item, col.field)).join(",")
      )
      .join("\n")}`;
    saveAs(
      new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
      `${filename}.csv`
    );
  };

  const displayedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const selectAllRows = () => {
    const allRowIds = filteredData?.map((item) => item[selectID]);
    setSelectedRows(allRowIds);
  };

  const clearSelectedRows = () => {
    setSelectedRows([]);
  };

  useEffect(() => {
    if (clearSelections) {
      setSelectedRows([]);
    }
  }, [clearSelections]);

  const handleDropdownToggle = (rowId: string) => {
    setActiveDropdown(activeDropdown === rowId ? null : rowId);
  };

  const handleDropdownClose = () => {
    setActiveDropdown(null);
  };

  const renderActionsDropdown = (item: any) => {
    const validActions = actions?.(item) || [];

    if (validActions?.length === 0) return null;

    return (
      <div
        className={styles.dropdown}
        ref={(el) => {
          dropdownRefs.current[item[selectID]] = el; // Store the reference
        }}
      >
        <span
          className={styles.dropdownToggle}
          onClick={() => handleDropdownToggle(item[selectID])}
        >
          <FaEllipsisH />
        </span>
        {activeDropdown === item[selectID] && (
          <div className={styles.dropdownMenu}>
            {validActions.map((action: any) => (
              <div
                key={action.name}
                className={styles.dropdownItem}
                onClick={() => {
                  action.onClick(item);
                  handleDropdownClose(); // Close the dropdown after clicking an action
                }}
              >
                {action.name}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        !Object?.values(dropdownRefs?.current)?.some((ref) =>
          ref?.contains(event.target as Node)
        )
      ) {
        handleDropdownClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const renderColumnValue = (item: any, column: Column) => {
    if (column.render) {
      return column?.render(item);
    } else {
      return getColumnValue(item, column.field);
    }
  };
  const handleFilterChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    const value = e.target.value;
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      [field]: value,
    }));
    setCurrentPage(1);
  };

  return (
    <div className={styles.tableContainer}>
      {Boolean(isFetching) ? (
        <div className={styles.loader}>
          <Rings
            visible={true}
            height="80"
            width="80"
            color="#6239bc"
            ariaLabel="rings-loading"
            wrapperStyle={{}}
            wrapperClass=""
          />
          <p>Please wait while we retrieve the data...</p>
        </div>
      ) : (
        <>
          <div className={styles.controls}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              className={styles.searchInput}
            />
            {exportBtns && (
              <div className={styles.buttonGroup}>
                <button onClick={downloadPDF} className={styles.pdfButton}>
                  Download PDF
                </button>
                <button onClick={downloadCSV} className={styles.csvButton}>
                  Download CSV
                </button>
              </div>
            )}
          </div>
          <table className={styles.customTable}>
            <thead>
              <tr className={styles.headerRow}>
                {showCheckbox && (
                  <th className={styles.headerCell}>
                    <input
                      type="checkbox"
                      title="checkbox"
                      className={styles.headerCheckbox}
                      checked={selectedRows.length === filteredData.length}
                      onChange={() =>
                        selectedRows.length === filteredData.length
                          ? clearSelectedRows()
                          : selectAllRows()
                      }
                    />
                  </th>
                )}
                {reverseAction && actions && (
                  <Fragment>
                    {actions.length > 0 && (
                      <th>
                        <FaCog />
                      </th>
                    )}
                  </Fragment>
                )}
                {columns.map((col) => (
                  <th key={col.field} className={styles.headerCell}>
                    <p>{col.title}</p>
                    {showFilter && (
                      <input
                        type="text"
                        value={filterValues[col?.field] || ""}
                        onChange={(e) => handleFilterChange(e, col?.field)}
                        placeholder={`Filter ${col?.title}`}
                        className={styles.filterInput}
                      />
                    )}
                  </th>
                ))}
                {!reverseAction && actions && (
                  <Fragment>
                    {actions.length > 0 && (
                      <th>
                        <FaCog />
                      </th>
                    )}
                  </Fragment>
                )}
              </tr>
            </thead>
            <tbody>
              {displayedData.map((item) => (
                <tr key={item[selectID]} className={styles.dataRow}>
                  {showCheckbox && (
                    <td className={styles.dataCell}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item[selectID])}
                        onChange={(e) =>
                          handleRowSelect(item[selectID], e.target.checked)
                        }
                        className={styles.rowCheckbox}
                      />
                    </td>
                  )}
                  {reverseAction && actions && (
                    <Fragment>
                      {actions.length > 0 && (
                        <td>{renderActionsDropdown(item)}</td>
                      )}
                    </Fragment>
                  )}
                  {columns.map((col) => (
                    <td key={col.field} className={styles.dataCell}>
                      {renderColumnValue(item, col)}
                    </td>
                  ))}
                  {!reverseAction && actions && (
                    <Fragment>
                      {actions.length > 0 && (
                        <td>{renderActionsDropdown(item)}</td>
                      )}
                    </Fragment>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.pagination}>
            <select
              style={{
                padding: "8px",
                border: "none",
                borderRadius: "4px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
                marginRight: "20px",
                fontSize: "10px",
              }}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              value={itemsPerPage}
            >
              <option value="10">10 Rows</option>
              <option value="20">20 Rows</option>
              <option value="50">50 Rows</option>
              <option value="100">100 Rows</option>
            </select>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className={styles.paginationButton}
            >
              Previous
            </button>
            <span className={styles.pageIndicator}>
              {currentPage} / {Math.ceil(filteredData.length / itemsPerPage)}
            </span>
            <button
              disabled={currentPage * itemsPerPage >= filteredData.length}
              onClick={() => setCurrentPage(currentPage + 1)}
              className={styles.paginationButton}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomTable;
